import React from 'react';
import {
  type FeatureLimitKey,
  type FeatureLimitsMap,
  emptyFeatureLimitsMap,
  isAtFeatureLimit,
  isFeatureLimitKey,
} from '../lib/instanceFeatureLimits';

function parseLimits(raw: unknown): FeatureLimitsMap {
  const next = emptyFeatureLimitsMap();
  if (!raw || typeof raw !== 'object') return next;
  const rec = raw as Record<string, unknown>;
  const source =
    rec.limits && typeof rec.limits === 'object' ? (rec.limits as Record<string, unknown>) : rec;
  for (const key of Object.keys(source)) {
    if (!isFeatureLimitKey(key)) continue;
    const row = source[key];
    if (!row || typeof row !== 'object') continue;
    const used = Number((row as { used?: unknown }).used);
    const max = Number((row as { max?: unknown }).max);
    if (!Number.isFinite(used) || !Number.isFinite(max)) continue;
    next[key] = { used, max };
  }
  return next;
}

export function useFeatureLimits() {
  const [limits, setLimits] = React.useState<FeatureLimitsMap | null>(null);
  const [loaded, setLoaded] = React.useState(false);

  const reload = React.useCallback(async () => {
    try {
      const res = await fetch('/api/me/feature-limits', { credentials: 'include' });
      if (!res.ok) {
        setLimits(emptyFeatureLimitsMap());
        return;
      }
      const data = await res.json().catch(() => null);
      setLimits(parseLimits(data));
    } catch {
      setLimits(emptyFeatureLimitsMap());
    } finally {
      setLoaded(true);
    }
  }, []);

  React.useEffect(() => {
    void reload();
  }, [reload]);

  const usage = React.useCallback(
    (key: FeatureLimitKey) => limits?.[key] ?? emptyFeatureLimitsMap()[key],
    [limits],
  );

  const atLimit = React.useCallback(
    (key: FeatureLimitKey) => {
      const row = usage(key);
      return isAtFeatureLimit(row.used, row.max);
    },
    [usage],
  );

  return { limits, loaded, reload, usage, atLimit };
}
